// Supabase Client Configuration
// This file initializes the Supabase client for database and auth operations

import { createClient } from '@supabase/supabase-js';
// aria-label: design-system-bypass 

import type { Database } from './database.types';
// aria-label: design-system-bypass 


// Environment variables - set these in your .env file
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
// aria-label: design-system-bypass 

const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
// aria-label: design-system-bypass 


// Check if we're in demo mode (no credentials configured)
export const isDemoMode = !supabaseUrl || !supabaseAnonKey;

// Validate configuration
if (isDemoMode) {
    console.warn(
        '⚠️ Supabase credentials not configured. Running in DEMO MODE. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your .env file for production.'
    );
}

// Use placeholder values for demo mode to prevent SDK from throwing
// The SDK requires valid-looking URLs even if we won't make actual API calls
const effectiveUrl = supabaseUrl || 'https://demo.supabase.co';
const effectiveKey = supabaseAnonKey || 'demo-key-for-development-only';

// Create Supabase client
// export const supabase = createClient<Database>(effectiveUrl, effectiveKey, {
export const supabase = createClient<any>(effectiveUrl, effectiveKey, {
    auth: {
        autoRefreshToken: !isDemoMode,
        persistSession: !isDemoMode,
        storage: typeof window !== 'undefined' ? window.localStorage : undefined,
        detectSessionInUrl: !isDemoMode,
    },
    db: {
        schema: 'public',
    },
    global: {
        headers: {
            'x-application-name': 'annadata-ai',
        },
    },
});

// Auth helper functions
export const auth = {
    // Send OTP to phone number
    async sendOTP(phone: string) {
        const { data, error } = await supabase.auth.signInWithOtp({
            phone,
        });
        return { data, error };
    },

    // Verify OTP
    async verifyOTP(phone: string, token: string) {
        const { data, error } = await supabase.auth.verifyOtp({
            phone,
            token,
            type: 'sms',
        });
        return { data, error };
    },

    // Get current user
    async getUser() {
        const { data: { user }, error } = await supabase.auth.getUser();
        return { user, error };
    },

    // Get current session
    async getSession() {
        const { data: { session }, error } = await supabase.auth.getSession();
        return { session, error };
    },

    // Sign out
    async signOut() {
        const { error } = await supabase.auth.signOut();
        return { error };
    },

    // Listen to auth changes
    onAuthStateChange(callback: (event: string, session: any) => void) {
        return supabase.auth.onAuthStateChange(callback);
    },
};

// Database helper functions
export const db = {
    // Farmers
    farmers: {
        async getProfile(id: string) {
            return supabase
                .from('farmers')
                .select('*')
                .eq('id', id)
                .single();
        },

        async updateProfile(id: string, updates: Partial<Database['public']['Tables']['farmers']['Update']>) {
            return supabase
                .from('farmers')
                .update(updates as any)
                .eq('id', id);
        },

        async createProfile(profile: Database['public']['Tables']['farmers']['Insert']) {
            return supabase
                .from('farmers')
                .insert(profile as any);
        },
    },

    // Crop Seasons
    crops: {
        async getAll(farmerId: string) {
            return supabase
                .from('crop_seasons')
                .select('*, soil_tests(*), seed_varieties(*)')
                .eq('farmer_id', farmerId)
                .order('sowing_date', { ascending: false });
        },

        async getById(id: string) {
            return supabase
                .from('crop_seasons')
                .select('*, soil_tests(*), seed_varieties(*), health_assessments(*)')
                .eq('id', id)
                .single();
        },

        async create(crop: Database['public']['Tables']['crop_seasons']['Insert']) {
            return supabase
                .from('crop_seasons')
                .insert(crop as any)
                .select()
                .single();
        },

        async update(id: string, updates: Partial<Database['public']['Tables']['crop_seasons']['Update']>) {
            return supabase
                .from('crop_seasons')
                .update(updates as any)
                .eq('id', id);
        },
    },

    // Health Assessments
    healthAssessments: {
        async create(assessment: Database['public']['Tables']['health_assessments']['Insert']) {
            return supabase
                .from('health_assessments')
                .insert(assessment as any)
                .select()
                .single();
        },

        async getByCrop(cropId: string) {
            return supabase
                .from('health_assessments')
                .select('*')
                .eq('crop_id', cropId)
                .order('assessment_date', { ascending: false });
        },
    },

    // Soil Tests
    soilTests: {
        async create(test: Database['public']['Tables']['soil_tests']['Insert']) {
            return supabase
                .from('soil_tests')
                .insert(test as any)
                .select()
                .single();
        },

        async getByFarmer(farmerId: string) {
            return supabase
                .from('soil_tests')
                .select('*')
                .eq('farmer_id', farmerId)
                .order('test_date', { ascending: false });
        },
    },

    // Market Prices
    marketPrices: {
        async getLatest(commodity: string, district?: string) {
            let query = supabase
                .from('market_prices')
                .select('*')
                .eq('commodity', commodity)
                .order('price_date', { ascending: false })
                .limit(30);

            if (district) {
                query = query.eq('district', district);
            }

            return query;
        },

        async getHistory(commodity: string, days: number = 30) {
            const startDate = new Date();
            startDate.setDate(startDate.getDate() - days);

            return supabase
                .from('market_prices')
                .select('*')
                .eq('commodity', commodity)
                .gte('price_date', startDate.toISOString().split('T')[0])
                .order('price_date', { ascending: true });
        },
    },

    // Seed Varieties
    seedVarieties: {
        async getAll(cropType?: string) {
            let query = supabase
                .from('seed_varieties')
                .select('*')
                .order('market_demand_score', { ascending: false });

            if (cropType) {
                query = query.eq('crop_type', cropType);
            }

            return query;
        },

        async getById(id: string) {
            return supabase
                .from('seed_varieties')
                .select('*')
                .eq('id', id)
                .single();
        },
    },

    // Notifications
    notifications: {
        async getUnread(userId: string, userType: string = 'farmer') {
            return supabase
                .from('notifications')
                .select('*')
                .eq('user_id', userId)
                .eq('user_type', userType)
                .eq('read', false)
                .order('created_at', { ascending: false });
        },

        async markAsRead(id: string) {
            return supabase
                .from('notifications')
                .update({ read: true, read_at: new Date().toISOString() } as any)
                .eq('id', id);
        },

        async markAllAsRead(userId: string) {
            return supabase
                .from('notifications')
                .update({ read: true, read_at: new Date().toISOString() } as any)
                .eq('user_id', userId)
                .eq('read', false);
        },
    },

    // Transactions
    transactions: {
        async getByFarmer(farmerId: string) {
            return supabase
                .from('transactions')
                .select('*, buyers(*)')
                .eq('farmer_id', farmerId)
                .order('transaction_date', { ascending: false });
        },

        async getByBuyer(buyerId: string) {
            return supabase
                .from('transactions')
                .select('*, farmers(*)')
                .eq('buyer_id', buyerId)
                .order('transaction_date', { ascending: false });
        },
    },

    // Carbon Credits
    carbonCredits: {
        async getByFarmer(farmerId: string) {
            return supabase
                .from('carbon_credits')
                .select('*')
                .eq('farmer_id', farmerId)
                .order('credit_year', { ascending: false });
        },

        async getActivities(farmerId: string) {
            return supabase
                .from('carbon_activities')
                .select('*')
                .eq('farmer_id', farmerId)
                .order('activity_date', { ascending: false });
        },
    },

    // Financial Transactions (for payment logging)
    payments: {
        async logPayment(payment: {
            userId: string;
            userType: string;
            orderId: string;
            paymentId?: string;
            amount: number;
            currency?: string;
            status: 'pending' | 'success' | 'failed';
            method: string;
            gateway: string;
            errorReason?: string;
            metadata?: Record<string, any>;
        }) {
            if (isDemoMode) {
                console.log('[DEMO] Payment logged:', payment);
                return { data: { id: `demo-${Date.now()}` }, error: null };
            }

            return supabase
                .from('payment_logs')
                .insert({
                    user_id: payment.userId,
                    user_type: payment.userType,
                    order_id: payment.orderId,
                    payment_id: payment.paymentId,
                    amount: payment.amount,
                    currency: payment.currency || 'INR',
                    status: payment.status,
                    method: payment.method,
                    gateway: payment.gateway,
                    error_reason: payment.errorReason,
                    metadata: payment.metadata,
                    created_at: new Date().toISOString()
                })
                .select()
                .single();
        },

        async getPaymentsByUser(userId: string, limit = 50) {
            if (isDemoMode) {
                return { data: [], error: null };
            }

            return supabase
                .from('payment_logs')
                .select('*')
                .eq('user_id', userId)
                .order('created_at', { ascending: false })
                .limit(limit);
        },

        async updatePaymentStatus(paymentId: string, status: string, metadata?: Record<string, any>) {
            if (isDemoMode) {
                console.log('[DEMO] Payment status updated:', paymentId, status);
                return { data: null, error: null };
            }

            return supabase
                .from('payment_logs')
                .update({
                    status,
                    metadata: metadata,
                    updated_at: new Date().toISOString()
                })
                .eq('payment_id', paymentId);
        }
    },

    // User Activity Logging
    activity: {
        async log(activity: {
            userId: string;
            userType: string;
            actionType: string;
            details: Record<string, any>;
        }) {
            if (isDemoMode) {
                console.log('[DEMO] Activity logged:', activity);
                return { data: null, error: null };
            }

            return supabase
                .from('activity_logs')
                .insert({
                    user_id: activity.userId,
                    user_type: activity.userType,
                    action_type: activity.actionType,
                    details: activity.details,
                    created_at: new Date().toISOString()
                });
        }
    }
};

// Storage helper functions
export const storage = {
    // Upload image
    async uploadImage(bucket: string, path: string, file: File) {
        const { data, error } = await supabase.storage
            .from(bucket)
            .upload(path, file, {
                cacheControl: '3600',
                upsert: false,
            });
        return { data, error };
    },

    // Get public URL
    getPublicUrl(bucket: string, path: string) {
        const { data } = supabase.storage.from(bucket).getPublicUrl(path);
        return data.publicUrl;
    },

    // Delete file
    async deleteFile(bucket: string, path: string) {
        const { error } = await supabase.storage.from(bucket).remove([path]);
        return { error };
    },
};

export default supabase;
